import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    systemColorContainerData,
    customSystemColorContainerData,
    SystemColorContainerType,
} from '@models/system-color';
import { SystemColorSocketService } from '@service/system-color-socket.service';
import { SystemColorService } from '@service/system-color.service';
import { DefaultColorsOrder, SystemColorType } from 'icreate/common/models/system-color';
import { Vessel } from 'icreate/common/models/vessel';
import { Subject, takeUntil } from 'rxjs';
import _ from 'lodash';
import { MessageDialogService } from '@service/message-dialog.service';
import { VesselService } from '@service/vessel.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-system-color',
    templateUrl: './system-color.component.html',
    styleUrls: ['./system-color.component.css'],
    standalone: false,
})
export class SystemColorComponent implements OnInit, OnDestroy {
    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean;

    systemColorContainerData;
    customSystemColorContainerData;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private systemColorService: SystemColorService,
        private systemColorSocketService: SystemColorSocketService,
        private messageDialogService: MessageDialogService,
        private vesselService: VesselService
    ) {}

    async ngOnInit() {
        try {
            let systemColorList = (await this.systemColorService.getAll(this.vessel.id)) as any;

            systemColorList.sort((a, b) => {
                return DefaultColorsOrder.indexOf(a.name) - DefaultColorsOrder.indexOf(b.name);
            });

            this.systemColorContainerData = systemColorContainerData.map(
                (systemColorContainer): SystemColorContainerType => {
                    return {
                        ...systemColorContainer,
                        color: systemColorList
                            .filter(systemColor => systemColor.type === systemColorContainer.type)
                            .map(systemColor => ({
                                ...systemColor,
                                isEditable: !(
                                    systemColor.type === SystemColorType.Alarm ||
                                    systemColor.type === SystemColorType.Sensor
                                ),
                            })),
                    };
                }
            );

            this.customSystemColorContainerData = {
                ...customSystemColorContainerData,
                color: systemColorList
                    .filter(systemColor => systemColor.type === customSystemColorContainerData.type)
                    .map(systemColor => ({
                        ...systemColor,
                        isEditable: true,
                    })),
            };
        } catch (error) {
            console.log(error);
        }

        this.systemColorSocketService
            .systemColorCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newSystemColor => {
                if (newSystemColor.vesselId === this.vessel.id) {
                    if (newSystemColor.type === SystemColorType.Custom) {
                        this.customSystemColorContainerData.color.push({
                            ...newSystemColor,
                            isEditable: true,
                        });
                    }
                    this.showMessageDialog('system-color.message-dialog.create.message', {
                        name: newSystemColor.name,
                    });
                }
            });

        this.systemColorSocketService
            .systemColorUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedSystemColor => {
                if (updatedSystemColor.vesselId === this.vessel.id) {
                    if (updatedSystemColor.type !== SystemColorType.Custom) {
                        let equal = true;
                        this.systemColorContainerData = this.systemColorContainerData.map(systemColorContainerData => {
                            const findIndex = systemColorContainerData.color.findIndex(
                                color => color.id === updatedSystemColor.id
                            );
                            const updatedSystemColorData = {
                                ...updatedSystemColor,
                                isEditable: !(
                                    updatedSystemColor.type === SystemColorType.Alarm ||
                                    updatedSystemColor.type === SystemColorType.Sensor
                                ),
                            };
                            if (findIndex !== -1) {
                                if (!_.isEqual(updatedSystemColorData, systemColorContainerData.color[findIndex]))
                                    equal = false;
                                return {
                                    ...systemColorContainerData,
                                    color: [
                                        ...systemColorContainerData.color.slice(0, findIndex),
                                        updatedSystemColorData,
                                        ...systemColorContainerData.color.slice(
                                            findIndex + 1,
                                            systemColorContainerData.color.length
                                        ),
                                    ],
                                };
                            } else {
                                return systemColorContainerData;
                            }
                        });
                        if (!equal) {
                            this.showMessageDialog('system-color.message-dialog.update.message', {
                                name: updatedSystemColor.name,
                            });
                        }
                    } else {
                        let equal = true;
                        const updatedSystemColorData = {
                            ...updatedSystemColor,
                            isEditable: true,
                        };
                        this.customSystemColorContainerData = {
                            ...this.customSystemColorContainerData,
                            color: this.customSystemColorContainerData.color.map(color => {
                                if (color.id === updatedSystemColor.id) {
                                    if (!_.isEqual(updatedSystemColorData, color)) equal = false;
                                    return updatedSystemColorData;
                                } else {
                                    return color;
                                }
                            }),
                        };
                        if (!equal) {
                            this.showMessageDialog('system-color.message-dialog.update.message', {
                                name: updatedSystemColor.name,
                            });
                        }
                    }
                }
            });

        this.systemColorSocketService
            .systemColorDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ systemColorId }) => {
                const findIndex = this.customSystemColorContainerData.color.findIndex(
                    color => color.id === systemColorId
                );
                if (findIndex !== -1) {
                    const deleteSystemColorName = this.customSystemColorContainerData.color[findIndex];
                    this.customSystemColorContainerData = {
                        ...this.customSystemColorContainerData,
                        color: [
                            ...this.customSystemColorContainerData.color.slice(0, findIndex),
                            ...this.customSystemColorContainerData.color.slice(
                                findIndex + 1,
                                this.customSystemColorContainerData.color.length
                            ),
                        ],
                    };
                    this.showMessageDialog('system-color.message-dialog.delete.message', {
                        name: deleteSystemColorName.name,
                    });
                }
            });
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    showMessageDialog(message, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }
}
